import { useState, useEffect } from 'react';
import { getGoogleApiToken } from '../../util';
import type { $TSFixMe } from '@calefy-inc/utilityTypes';

type GoogleMapsLoadResult = {
  google: $TSFixMe;
  placesLibrary: $TSFixMe;
};

const getWindowGoogle = (): $TSFixMe | undefined => {
  // @ts-expect-error google is injected by the Maps JS API script
  return window.google;
};

let googleMapsLoadPromise: Promise<GoogleMapsLoadResult> | null = null;

const resolvePlacesLibrary = async (
  google: $TSFixMe,
): Promise<$TSFixMe | undefined> => {
  if (!google?.maps) {
    return undefined;
  }

  if (google.maps.places?.AutocompleteSuggestion) {
    return google.maps.places;
  }

  if (!google.maps.importLibrary) {
    return undefined;
  }

  try {
    const library = await google.maps.importLibrary('places');
    return library?.AutocompleteSuggestion ? library : google.maps.places;
  } catch {
    return undefined;
  }
};

const waitForGoogleMapsScript = async (apiKey: string): Promise<void> => {
  if (getWindowGoogle()?.maps) {
    return;
  }

  let script = document.getElementById(
    'google-script',
  ) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry,drawing`;
    script.id = 'google-script';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  if (getWindowGoogle()?.maps) {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const onLoad = () => {
      resolve();
    };
    const onError = () => {
      reject(new Error('Failed to load Google Maps script'));
    };

    script?.addEventListener('load', onLoad, { once: true });
    script?.addEventListener('error', onError, { once: true });
  });
};

const loadGoogleMapsApi = async (
  apiKey: string,
): Promise<GoogleMapsLoadResult> => {
  if (googleMapsLoadPromise) {
    return googleMapsLoadPromise;
  }

  googleMapsLoadPromise = (async () => {
    try {
      await waitForGoogleMapsScript(apiKey);

      const windowGoogle = getWindowGoogle();
      if (!windowGoogle?.maps) {
        throw new Error('Google Maps API failed to initialize');
      }

      const placesLibrary = await resolvePlacesLibrary(windowGoogle);
      if (!placesLibrary?.AutocompleteSuggestion) {
        throw new Error(
          'Google Places library loaded but AutocompleteSuggestion is unavailable',
        );
      }

      return {
        google: windowGoogle,
        placesLibrary,
      };
    } catch (error) {
      googleMapsLoadPromise = null;
      throw error;
    }
  })();

  return googleMapsLoadPromise;
};

export const useGoogleMaps = () => {
  const apiKey = getGoogleApiToken();
  const [google, setGoogle] = useState<$TSFixMe>();
  const [placesLibrary, setPlacesLibrary] = useState<$TSFixMe>();
  const [loadError, setLoadError] = useState<Error>();

  useEffect(() => {
    if (!apiKey) {
      return;
    }

    let active = true;

    const load = async () => {
      try {
        const result = await loadGoogleMapsApi(apiKey);
        if (!active) {
          return;
        }

        setGoogle(result.google);
        setPlacesLibrary(result.placesLibrary);
        setLoadError(undefined);
      } catch (error) {
        if (!active) {
          return;
        }

        setLoadError(error instanceof Error ? error : new Error(String(error)));
      }
    };

    void load();

    return () => {
      active = false;
    };
  }, [apiKey]);

  return {
    google,
    placesLibrary,
    loadError,
    GOOGLE_API_KEY: apiKey,
  };
};
